// =============================================================================
// io/discord/testWebhook.ts — the ping that proves a webhook works.
// -----------------------------------------------------------------------------
// ── 🚨 A SUCCESSFUL TEST IS WHAT SAVES THE URL ──
//
// There is no separate Save. The old client does the same, and the reasoning is
// worth keeping: a stored webhook that has never posted is indistinguishable on
// screen from one that works, right up until the trader presses "Post to
// Discord" on a trade they wanted to share and nothing arrives.
//
// ── ⚠ THE BROWSER POSTS THIS DIRECTLY, AND CSP HAS TO ALLOW IT ──
//
// `connect-src` in `public/_headers` names `discord.com` and `discordapp.com`
// for this one call. Without them the fetch is refused by the browser BEFORE it
// leaves, and the failure arrives here as a bare `TypeError: Failed to fetch` —
// which is indistinguishable from an offline laptop and would have been
// reported to the user as "Discord did not accept it". The message below says
// what a fetch-level failure actually means instead of guessing.
//
// ⚠ NOT PROXIED THROUGH AN EDGE FUNCTION, deliberately. The URL is the user's
// own credential for their own channel; routing it through our server would
// mean holding it there and give a second place for it to leak.
// =============================================================================

export interface WebhookTestResult {
  ok: boolean;
  /** User-facing, already a full sentence. Null when `ok`. */
  error: string | null;
}

/**
 * Post a small message to `url`.
 *
 * @param label the slot's name, so the message says which one connected.
 */
export async function pingDiscordWebhook(
  url: string, label: string,
): Promise<WebhookTestResult> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content: `✅ TRADECOACH · the “${label}” webhook is connected. `
          + 'This is a test message — you can delete it.',
      }),
    });
  } catch {
    return {
      ok: false,
      error: 'The test could not be sent. That usually means no connection — '
        + 'it can also mean the browser blocked the request, which is not '
        + 'something the URL itself can fix.',
    };
  }

  // ⚠ 204 IS THE SUCCESS DISCORD ACTUALLY RETURNS. `res.ok` covers it, and it is
  // checked explicitly as well because a webhook posting with `?wait=true`
  // answers 200 with a body — both are success and neither is an error.
  if (res.ok || res.status === 204) return { ok: true, error: null };

  if (res.status === 401 || res.status === 403 || res.status === 404) {
    return {
      ok: false,
      error: 'Discord rejected that webhook. It has probably been deleted or '
        + 'regenerated — make a new one in the channel’s settings and paste it here.',
    };
  }
  if (res.status === 429) {
    return {
      ok: false,
      error: 'Discord is rate-limiting that channel. Wait a moment and test again.',
    };
  }
  return {
    ok: false,
    error: `Discord answered ${res.status}. The webhook was not saved.`,
  };
}
